export interface IEmbarkMessagePayload {
    isEmbark: boolean;
    isDarkMode: boolean;
    locale: string;
    designToken: Record<string, unknown>;
    designTokenDarkMode: Record<string, unknown>;
}

export const EMBARK_IFRAME_MODAL_MESSAGE = 'embarkIFrameModal';

/** Keys stripped from design token payloads to prevent arbitrary component/HTML injection. */
const DENYLISTED_KEYS = new Set([
    '__proto__',
    'prototype',
    'constructor',
    'component',
    'components',
    'defaultProps',
    'slots',
    'slotProps',
    'srcDoc',
    'dangerouslySetInnerHTML',
    'children',
    'render',
]);

const MAX_SANITIZE_DEPTH = 6;
/** Blocks obvious HTML/script payloads in string values. */
const HTML_LIKE_PATTERN = /[<>]/;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/** Recursively drops anything that isn't a safe primitive, array, or plain object. */
const sanitize = (value: unknown, depth: number): unknown => {
    if (depth > MAX_SANITIZE_DEPTH || typeof value === 'function') {
        return undefined;
    }
    if (typeof value === 'string') {
        return HTML_LIKE_PATTERN.test(value) ? undefined : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => sanitize(item, depth + 1)).filter((item) => item !== undefined);
    }
    if (isPlainObject(value)) {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([key]) => !DENYLISTED_KEYS.has(key) && !key.startsWith('on'))
                .map(([key, entry]) => [key, sanitize(entry, depth + 1)])
                .filter(([, entry]) => entry !== undefined)
        );
    }
    return undefined;
};

/** Strips dangerous keys, functions, and HTML-like strings from a design token payload. */
export const sanitizeDesignToken = (value: unknown): Record<string, unknown> => {
    const sanitized = sanitize(value, 0);
    return isPlainObject(sanitized) ? sanitized : {};
};

/**
 * Matches any `https://*.emburse.com` or `https://*.emburse.app` subdomain, including nested
 * hosts such as `https://main.dev.pro.emburse.app`. Mirrors the `frame-ancestors` CSP in `nginx.conf`.
 * The bare apex (`https://emburse.com`, `https://emburse.app`) is not trusted.
 */
const TRUSTED_PARENT_ORIGIN_PATTERN = /^https:\/\/([a-z0-9-]+\.)+emburse\.(com|app)$/i;

/** Matches `http(s)://localhost` / `127.0.0.1` (any port). Trusted by default for local dev. */
const LOCALHOST_ORIGIN_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i;

/** Extra exact origins trusted via `REACT_APP_TRUSTED_PARENT_ORIGINS` (comma-separated). */
const getExtraTrustedOrigins = (): string[] => {
    return (process.env.REACT_APP_TRUSTED_PARENT_ORIGINS ?? '')
        .split(',')
        .map((origin) => origin.trim())
        .filter((origin) => origin.length > 0);
};

/** True only when this page itself is served from localhost/127.0.0.1 (local dev/test). */
const isLocalDeployment = (): boolean => {
    return LOCALHOST_ORIGIN_PATTERN.test(window.location.origin);
};

/** An origin is trusted if it's same-origin, `*.emburse.com`, `*.emburse.app`, localhost (only on local deployments), or explicitly allow-listed. */
export const isTrustedParentOrigin = (origin: string): boolean => {
    return origin === window.location.origin
        || TRUSTED_PARENT_ORIGIN_PATTERN.test(origin)
        || (isLocalDeployment() && LOCALHOST_ORIGIN_PATTERN.test(origin))
        || getExtraTrustedOrigins().includes(origin);
};

/** A message is trusted if it's from a trusted origin AND sent by window.parent or window.top. */
export const isTrustedEmbarkMessageEvent = (event: MessageEvent): boolean => {
    return (event.source === window.parent || event.source === window.top)
        && isTrustedParentOrigin(event.origin);
};

const isValidLocale = (locale: unknown): locale is string => {
    return typeof locale === 'string' && /^[a-zA-Z-]{2,10}$/.test(locale);
};

/**
 * Validates the sender and shape of an `embarkIFrameModal` message, returning a sanitized
 * payload, or `null` if the message should be ignored (wrong message name, untrusted
 * source/origin, or malformed payload).
 */
export const parseEmbarkIFrameModalPayload = (event: MessageEvent): IEmbarkMessagePayload | null => {
    const value = event?.data?.value;

    if (event?.data?.message !== EMBARK_IFRAME_MODAL_MESSAGE
        || !isTrustedEmbarkMessageEvent(event)
        || !isPlainObject(value)
        || typeof value.isEmbark !== 'boolean'
        || typeof value.isDarkMode !== 'boolean'
        || !isValidLocale(value.locale)) {
        return null;
    }

    return {
        isEmbark: value.isEmbark,
        isDarkMode: value.isDarkMode,
        locale: value.locale,
        designToken: sanitizeDesignToken(value.designToken),
        designTokenDarkMode: sanitizeDesignToken(value.designTokenDarkMode),
    };
};